import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Rocket,
  Wand2,
  Send,
  FileImage,
  Contact,
  Lightbulb,
  Share2,
  Users,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  Lock,
  ChartColumn,
  Calculator,
  CheckCircle,
  Clock,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { getCurrentUser, getCachedUserSync } from "@/services/currentUserService";

const myListIQLogo = "/lovable-uploads/bca63d36-de76-4733-8b0b-c1a03da629a0.png";

interface UserData {
  id?: string;
  email?: string;
  name?: string;
  plan_name?:
  | "Pro"
  | "Pro+"
  | "Pro Annual"
  | "Pro+ Annual"
  | "Starter"
  | "Starter Annual"
  | "Free";
  is_superuser?: boolean;
  is_staff?: boolean;
  is_in_free_trial?: boolean;
  trial_end_date?: string;
  can_send_flyer?: boolean;
  can_send_bulk_flyer?: boolean;
  can_upload_csv_flyer?: boolean;
}

const mapApiUser = (data: any): UserData => ({
  id: data.id || data._id,
  email: data.email,
  name:
    `${data.first_name || ""} ${data.last_name || ""}`.trim() ||
    data.name,
  plan_name: data.plan_name || "Free",
  is_superuser: !!data.is_superuser,
  is_staff: !!data.is_staff,
  is_in_free_trial: data.is_in_free_trial || false,
  trial_end_date: data.trial_end_date || undefined,
  can_send_flyer: !!data.can_send_flyer,
  can_send_bulk_flyer: !!data.can_send_bulk_flyer,
  can_upload_csv_flyer: !!data.can_upload_csv_flyer,
});

interface SidebarProps {
  className?: string;
  isBuilderPage?: boolean;
  onNavigateAttempt?: (path: string) => void;
  registerMobileToggle?: (fn: () => void) => void;
  currentStep?: number;
  onLogoutAttemptOnStep8?: () => void;
  onCollapsedChange?: (collapsed: boolean) => void; // NEW
}

export function Sidebar({
  className,
  isBuilderPage = false,
  onNavigateAttempt,
  registerMobileToggle,
  currentStep,
  onLogoutAttemptOnStep8,
  onCollapsedChange, // ← make sure this is destructured
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLaunchIQOpen, setIsLaunchIQOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useIsMobile();
  const [isDesktopSidebar, setIsDesktopSidebar] = useState(false);

  const [user, setUser] = useState<UserData | null>(() => {
    const cached = getCachedUserSync();
    return cached ? mapApiUser(cached) : null;
  });
  const [userLoading, setUserLoading] = useState(() => !getCachedUserSync());
  const [isCollapsed, setIsCollapsed] = useState(() =>
    localStorage.getItem("sidebar_collapsed") === "true"
  );
  const logoutDialogTimerRef = useRef<number | null>(null);

  const restoreBodyInteractivity = () => {
    if (document.body.style.pointerEvents === "none") {
      document.body.style.pointerEvents = "";
    }
  };

  const clearLogoutDialogTimer = () => {
    if (logoutDialogTimerRef.current !== null) {
      window.clearTimeout(logoutDialogTimerRef.current);
      logoutDialogTimerRef.current = null;
    }
  };

  const openLogoutConfirm = () => {
    clearLogoutDialogTimer();
    setMobileOpen(false);
    logoutDialogTimerRef.current = window.setTimeout(() => {
      setShowLogoutConfirm(true);
      logoutDialogTimerRef.current = null;
    }, 320);
  };

  const closeLogoutConfirm = () => {
    clearLogoutDialogTimer();
    setShowLogoutConfirm(false);
    setMobileOpen(false);
    window.setTimeout(restoreBodyInteractivity, 0);
    window.requestAnimationFrame(restoreBodyInteractivity);
    window.requestAnimationFrame(() => window.requestAnimationFrame(restoreBodyInteractivity));
  };

  useEffect(() => {
    return () => {
      clearLogoutDialogTimer();
    };
  }, []);

  // Collapse only applies on lg+ where the persistent sidebar is visible
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktopSidebar(mql.matches);
    mql.addEventListener("change", update);
    update();
    return () => mql.removeEventListener("change", update);
  }, []);

  const showCollapsed = isCollapsed && isDesktopSidebar;

  // Register the mobile toggle function with the parent (PageHeader uses it)
  useEffect(() => {
    if (registerMobileToggle) {
      registerMobileToggle(() => setMobileOpen((prev) => !prev));
    }
  }, [registerMobileToggle]);

  // Close mobile drawer when switching to desktop layout (prevents stuck overlay)
  useEffect(() => {
    if (isDesktopSidebar && mobileOpen) {
      setMobileOpen(false);
    }
  }, [isDesktopSidebar, mobileOpen]);

  useEffect(() => {
    let cancelled = false;

    const fetchUser = async () => {
      const hasCachedUser = !!getCachedUserSync();
      if (!hasCachedUser) setUserLoading(true);

      try {
        const data = await getCurrentUser();
        if (cancelled) return;
        setUser(mapApiUser(data));
      } catch (err: any) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          localStorage.clear();
          if (location.pathname !== "/signup") {
            navigate("/signup", { replace: true });
          }
        }
      } finally {
        if (!cancelled) setUserLoading(false);
      }
    };

    fetchUser();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // useEffect(() => {
  //   const saved = localStorage.getItem('sidebar_collapsed');
  //   if (saved === 'true') setIsCollapsed(true);
  // }, []);

  const handleToggleCollapse = () => {
    // On small/tablet screens the collapse control should close the drawer, not collapse layout
    if (!isDesktopSidebar) {
      setMobileOpen(false);
      return;
    }
    const next = !isCollapsed;
    setIsCollapsed(next);
    localStorage.setItem("sidebar_collapsed", String(next));
    onCollapsedChange?.(next);
  };


  const isOnFreeTrial = user?.is_in_free_trial || false;
  const hasTrialEnded = user?.trial_end_date
    ? new Date(user.trial_end_date) < new Date()
    : false;
  const hasProAccess =
    user?.plan_name === "Pro" ||
    user?.plan_name === "Pro+" ||
    user?.plan_name === "Pro Annual" ||
    user?.plan_name === "Pro+ Annual";
  const hasMyHubAccess =
    hasProAccess ||
    (isOnFreeTrial &&
      (user?.plan_name === "Starter" ||
        user?.plan_name === "Starter Annual"));

  const handleNavClick = () => {
    if (isMobile) setMobileOpen(false);
  };

  const handleRestrictedNavigation = (path: string) => {
    if (path === "/agent" && !hasMyHubAccess) {
      setShowUpgradeModal(true);
      return;
    }
    if (path !== "/agent" && !hasProAccess) {
      setShowUpgradeModal(true);
      return;
    }
    if (isBuilderPage && onNavigateAttempt) {
      onNavigateAttempt(path);
      return;
    }
    handleNavClick();
    navigate(path);
  };

  const handleComingSoonClick = () => setShowComingSoonModal(true);

  const SafeNavItem: React.FC<{
    to: string;
    children: React.ReactNode;
    icon?: React.ComponentType<any>;
    className?: string;
  }> = ({ to, children, icon: Icon, className }) => {
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      if (isBuilderPage && onNavigateAttempt) {
        onNavigateAttempt(to);
      } else {
        handleNavClick();
        navigate(to);
      }
    };

    const isActive = location.pathname === to;

    return (
      <div onClick={handleClick} className="cursor-pointer">
        <div
          className={cn(
            "flex items-center px-4 py-2.5 rounded-lg text-sm font-medium transition",
            isActive
              ? "bg-blue-500 text-white rounded-full"
              : "text-black-700 hover:bg-gray-100 rounded-lg",
            className
          )}
        >
          {Icon && (
            <Icon
              className={cn(
                "mr-3 h-5 w-5",
                isActive ? "text-white" : "text-black-600"
              )}
            />
          )}
          {children}
        </div>
      </div>
    );
  };

  const RestrictedNavItem: React.FC<{
    to: string;
    children: React.ReactNode;
    icon: React.ComponentType<any>;
    isLocked: boolean;
  }> = ({ to, children, icon: Icon, isLocked }) => {
    const isActive = location.pathname === to;
    return (
      <div
        onClick={() => handleRestrictedNavigation(to)}
        className={cn(
          "cursor-pointer flex items-center px-4 py-2.5 rounded-lg text-sm font-medium transition",
          isLocked
            ? "text-gray-400 cursor-not-allowed"
            : isActive
              ? "bg-blue-500 text-white rounded-full"
              : "text-black-700 hover:bg-gray-100"
        )}
      >
        <Icon
          className={cn(
            "mr-3 h-5 w-5",
            isLocked ? "text-gray-400" : isActive ? "text-white" : "text-black-600"
          )}
        />
        <span className="flex-1">{children}</span>
        {isLocked && <Lock className="h-4 w-4 ml-auto text-gray-400" />}
      </div>
    );
  };

  const ComingSoonNavItem: React.FC<{
    children: React.ReactNode;
    icon: React.ComponentType<any>;
  }> = ({ children, icon: Icon }) => (
    <div
      onClick={handleComingSoonClick}
      className="cursor-pointer flex items-center px-4 py-2.5 rounded-lg text-sm font-medium transition text-gray-400 hover:bg-gray-50"
    >
      <Icon className="mr-3 h-5 w-5 text-gray-400" />
      <span className="flex-1">{children}</span>
      {/* <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-blue-100 to-blue-100 text-blue-700 rounded-full border border-blue-200">
        Coming Soon
      </span> */}
    </div>
  );

  const navigation = [
    { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
    {
      name: "LaunchIQ",
      icon: Rocket,
      children: [
        { name: "LaunchIQ Builder", href: "/builder", icon: Wand2 },
        { name: "LaunchIQ Management", href: "/launches", icon: Send },
      ],
    },
    { name: "CMA", href: "/cma", icon: Calculator },
    { name: "My Hub", href: "/agent", icon: Contact, requiresPro: true },
    {
      name: "Insight & Analytics",
      href: "/analytics",
      icon: ChartColumn,
      requiresPro: true,
    },
    {
      name: "Social Media Uploads",
      href: "/social-media",
      icon: Share2,
      comingSoon: false,
    },
  ];

  const sidebarContent = (
    <div
      // The sidebarContent div — change min-h-screen to h-screen
      className={cn(
        "relative flex flex-col bg-white border-r text-black-900",
        "border-gray-200 transition-all duration-300",
        isDesktopSidebar
          ? isCollapsed
            ? "w-16 h-screen"
            : "w-56 h-screen"
          : "w-full h-full border-r-0",
        className
      )}
    >
      {/* Logo header */}
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-gray-200 overflow-hidden">
        <div className="flex items-center space-x-1 min-w-0">
          <Link
            to="/"
            className="flex items-center space-x-1 cursor-pointer"
            onClick={() => setMobileOpen(false)}
          >
            <img
              src={myListIQLogo}
              alt="MyListIQ"
              className="h-12 w-12 object-contain rounded-md flex-shrink-0"
            />
            {/* Hide text when collapsed (desktop only) */}
            {!showCollapsed && (
              <div>
                <h2 className="text-base font-bold text-black-900">
                  MyList<span className="text-cta">IQ</span>
                </h2>
                <p className="text-[10px] text-black-600 whitespace-nowrap">
                  Easy.Affordable.<span className="text-red-600">Smarter.</span>
                </p>
              </div>
            )}
          </Link>
        </div>
        {/* Mobile close button */}
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-900 flex-shrink-0"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 min-h-0 px-2 py-6 space-y-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
        {navigation.map((item) => {
          if (item.children) {
            return (
              <div key={item.name} className="space-y-1">
                {!showCollapsed && (
                  <button
                    onClick={() => setIsLaunchIQOpen(!isLaunchIQOpen)}
                    className={cn(
                      "flex items-center w-full px-3 py-2.5 rounded-lg text-sm font-medium text-black-700 hover:bg-gray-100 transition"
                    )}
                    title={item.name}
                  >
                    <item.icon className="h-5 w-5 text-black-600 flex-shrink-0 mr-3" />
                    {item.name}
                    <ChevronDown
                      className={cn(
                        "ml-auto h-4 w-4 text-black-500 transition-transform duration-300",
                        isLaunchIQOpen ? "rotate-180" : ""
                      )}
                    />
                  </button>
                )}
                {!showCollapsed && (
                  <div
                    className={cn(
                      "overflow-hidden transition-all duration-300",
                      isLaunchIQOpen ? "max-h-96" : "max-h-0"
                    )}
                  >
                    <div className="ml-8 flex flex-col space-y-1 mt-1">
                      {item.children.map((child) => (
                        <SafeNavItem
                          key={child.name}
                          to={child.href}
                          icon={child.icon}
                        >
                          {child.name}
                        </SafeNavItem>
                      ))}
                    </div>
                  </div>
                )}
                {/* Collapsed: show child icons individually */}
                {showCollapsed && (
                  <div className="flex flex-col space-y-1">
                    {item.children.map((child) => {
                      const isActive = location.pathname === child.href;
                      return (
                        <div
                          key={child.name}
                          onClick={() => {
                            if (isBuilderPage && onNavigateAttempt) {
                              onNavigateAttempt(child.href);
                            } else {
                              navigate(child.href);
                            }
                          }}
                          title={child.name}
                          className={cn(
                            "flex items-center justify-center p-2.5 rounded-lg cursor-pointer transition",
                            isActive
                              ? "bg-blue-500 text-white rounded-full"
                              : "text-black-700 hover:bg-gray-100"
                          )}
                        >
                          <child.icon className={cn("h-5 w-5", isActive ? "text-white" : "text-black-600")} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          if (item.comingSoon) {
            return (
              <div
                key={item.name}
                onClick={handleComingSoonClick}
                className={cn(
                  "cursor-pointer flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition text-gray-400 hover:bg-gray-50",
                  showCollapsed && "justify-center px-2"
                )}
                title={showCollapsed ? item.name : undefined}
              >
                <item.icon className={cn("h-5 w-5 text-gray-400 flex-shrink-0", !showCollapsed && "mr-3")} />
                {!showCollapsed && item.name}
              </div>
            );
          }

          if (item.requiresPro) {
            if (userLoading) {
              return (
                <div
                  key={item.name}
                  className={cn(
                    "flex items-center px-3 py-2.5 rounded-lg text-sm font-medium text-gray-400",
                    showCollapsed && "justify-center px-2"
                  )}
                >
                  <item.icon className={cn("h-5 w-5 flex-shrink-0", !showCollapsed && "mr-3")} />
                  {!showCollapsed && item.name}
                </div>
              );
            }
            const isLocked =
              item.name === "My Hub" ? !hasMyHubAccess : !hasProAccess;
            const isActive = location.pathname === item.href;
            return (
              <div
                key={item.name}
                onClick={() => handleRestrictedNavigation(item.href)}
                title={showCollapsed ? item.name : undefined}
                className={cn(
                  "cursor-pointer flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition",
                  showCollapsed && "justify-center px-2",
                  isLocked
                    ? "text-gray-400 cursor-not-allowed"
                    : isActive
                      ? "bg-blue-500 text-white rounded-full"
                      : "text-black-700 hover:bg-gray-100"
                )}
              >
                <item.icon
                  className={cn(
                    "h-5 w-5 flex-shrink-0",
                    !showCollapsed && "mr-3",
                    isLocked ? "text-gray-400" : isActive ? "text-white" : "text-black-600"
                  )}
                />
                {!showCollapsed && (
                  <>
                    <span className="flex-1">{item.name}</span>
                    {isLocked && <Lock className="h-4 w-4 ml-auto text-gray-400" />}
                  </>
                )}
              </div>
            );
          }

          // Normal nav item (CMA stays highlighted on its /cma/:id detail pages)
          const isActive =
            location.pathname === item.href ||
            (item.href === "/cma" && location.pathname.startsWith("/cma/"));
          return (
            <div
              key={item.name}
              onClick={() => {
                if (isBuilderPage && onNavigateAttempt) {
                  onNavigateAttempt(item.href);
                } else {
                  handleNavClick();
                  navigate(item.href);
                }
              }}
              title={showCollapsed ? item.name : undefined}
              className={cn(
                "cursor-pointer flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition",
                showCollapsed && "justify-center px-2",
                isActive
                  ? "bg-blue-500 text-white rounded-full"
                  : "text-black-700 hover:bg-gray-100"
              )}
            >
              <item.icon
                className={cn(
                  "h-5 w-5 flex-shrink-0",
                  !showCollapsed && "mr-3",
                  isActive ? "text-white" : "text-black-600"
                )}
              />
              {!showCollapsed && item.name}
            </div>
          );
        })}

        {/* Admin section */}
        {(user?.is_superuser || user?.is_staff) && (
          <>
            {!showCollapsed && (
              <div className="pt-8 pb-2 px-4">
                <p className="text-xs font-semibold text-black-500 uppercase tracking-wider">ADMIN</p>
              </div>
            )}
            {showCollapsed && <div className="pt-4" />}
            <div
              onClick={() => {
                if (isBuilderPage && onNavigateAttempt) onNavigateAttempt("/manage/users");
                else navigate("/manage/users");
              }}
              title={showCollapsed ? "Users" : undefined}
              className={cn(
                "cursor-pointer flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition",
                showCollapsed && "justify-center px-2",
                location.pathname === "/manage/users"
                  ? "bg-blue-500 text-white rounded-full"
                  : "text-black-700 hover:bg-gray-100"
              )}
            >
              <Users className={cn("h-5 w-5 flex-shrink-0", !showCollapsed && "mr-3", location.pathname === "/manage/users" ? "text-white" : "text-black-600")} />
              {!showCollapsed && "Users"}
            </div>
            <div
              onClick={() => {
                if (isBuilderPage && onNavigateAttempt) onNavigateAttempt("/manage/agents");
                else navigate("/manage/agents");
              }}
              title={showCollapsed ? "Agents" : undefined}
              className={cn(
                "cursor-pointer flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition",
                showCollapsed && "justify-center px-2",
                location.pathname === "/manage/agents"
                  ? "bg-blue-500 text-white rounded-full"
                  : "text-black-700 hover:bg-gray-100"
              )}
            >
              <Users className={cn("h-5 w-5 flex-shrink-0", !showCollapsed && "mr-3", location.pathname === "/manage/agents" ? "text-white" : "text-black-600")} />
              {!showCollapsed && "Agents"}
            </div>
            {(user?.is_superuser || user?.can_send_flyer || user?.can_send_bulk_flyer || user?.can_upload_csv_flyer) && (
              <div
                onClick={() => {
                  if (isBuilderPage && onNavigateAttempt) onNavigateAttempt("/manage/flyer-control");
                  else navigate("/manage/flyer-control");
                }}
                title={showCollapsed ? "Flyer Control" : undefined}
                className={cn(
                  "cursor-pointer flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition",
                  showCollapsed && "justify-center px-2",
                  location.pathname === "/manage/flyer-control"
                    ? "bg-blue-500 text-white rounded-full"
                    : "text-black-700 hover:bg-gray-100"
                )}
              >
                <Send className={cn("h-5 w-5 flex-shrink-0", !showCollapsed && "mr-3", location.pathname === "/manage/flyer-control" ? "text-white" : "text-black-600")} />
                {!showCollapsed && "Flyer Control"}
              </div>
            )}
          </>
        )}

        {/* Settings divider */}
        {!showCollapsed && (
          <div className="pt-8 pb-2 px-4">
            <p className="text-xs font-semibold text-black-500 uppercase tracking-wider">SETTINGS</p>
          </div>
        )}
        {showCollapsed && <div className="pt-4" />}

        {/* Settings link */}
        {(() => {
          const isActive = location.pathname === "/settings";
          return (
            <div
              onClick={() => {
                if (isBuilderPage && onNavigateAttempt) onNavigateAttempt("/settings");
                else { handleNavClick(); navigate("/settings"); }
              }}
              title={showCollapsed ? "Account Settings" : undefined}
              className={cn(
                "cursor-pointer flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition",
                showCollapsed && "justify-center px-2",
                isActive ? "bg-blue-500 text-white rounded-full" : "text-black-700 hover:bg-gray-100"
              )}
            >
              <Settings
                className={cn(
                  "h-5 w-5 flex-shrink-0",
                  !showCollapsed && "mr-3",
                  isActive ? "text-white" : "text-black-600"
                )}
              />
              {!showCollapsed && "Account Settings"}
            </div>
          );
        })()}

        {/* Logout section */}
        {/* Logout section */}
        <div
          className={cn(
            "px-2 pb-4 border-t border-gray-100 pt-2",
            showCollapsed ? "flex flex-col items-center gap-1" : "flex items-center gap-1"
          )}
        >
          <button
            onClick={() => {
              if (currentStep === 8 && onLogoutAttemptOnStep8) {
                onLogoutAttemptOnStep8();
              } else {
                openLogoutConfirm();
              }
            }}
            title={showCollapsed ? "Logout" : undefined}
            className={cn(
              "flex items-center px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition",
              showCollapsed
                ? "w-auto justify-center"
                : "flex-1 w-full"
            )}
          >
            <LogOut
              className={cn(
                "h-5 w-5 flex-shrink-0",
                !showCollapsed && "mr-3"
              )}
            />
            {!showCollapsed && "Logout"}
          </button>

          {/* Collapse button — desktop only (lg+) */}
          {isDesktopSidebar && (
            <button
              onClick={handleToggleCollapse}
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              className={cn(
                "flex items-center justify-center",
                "w-7 h-7 rounded-full flex-shrink-0",
                "border border-gray-200 bg-white",
                "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
                "transition-colors shadow-sm"
              )}
            >
              {isCollapsed ? (
                <ChevronRight className="h-3.5 w-3.5" />
              ) : (
                <ChevronLeft className="h-3.5 w-3.5" />
              )}
            </button>
          )}
        </div>
      </nav>

      {/* ── Collapse toggle button on right edge ── */}
      {/* Logout + Collapse toggle */}

    </div>
  );

  return (
    <>
      <Sheet
        open={mobileOpen && !isDesktopSidebar}
        onOpenChange={setMobileOpen}
      >
        <SheetContent
          side="left"
          onPointerDownOutside={() => setMobileOpen(false)}
          onInteractOutside={() => setMobileOpen(false)}
          className="p-0 w-56 bg-white text-black-900 border-gray-200 [&>button]:hidden overflow-hidden"
        >
          {sidebarContent}
        </SheetContent>
      </Sheet>


      <div
        className={cn(
          "hidden lg:flex lg:sticky lg:top-0 lg:h-screen lg:self-start flex-shrink-0 overflow-hidden transition-all duration-300",
          isCollapsed ? "w-16 max-w-[4rem]" : "w-56 max-w-[14rem]"
        )}
      >
        {sidebarContent}
      </div>

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
              {hasTrialEnded && user?.plan_name?.includes("Starter")
                ? "Trial Ended"
                : "Upgrade Required"}
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm sm:text-base">
              {hasTrialEnded && user?.plan_name?.includes("Starter") ? (
                <>
                  Your free trial has ended. My Hub is only available on{" "}
                  <strong className="text-gray-900">Pro</strong> or{" "}
                  <strong className="text-gray-900">Pro+</strong> plans.
                </>
              ) : (
                <>
                  This feature is available only on{" "}
                  <strong className="text-gray-900">Pro</strong> or{" "}
                  <strong className="text-gray-900">Pro+</strong> plans.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 sm:py-4">
            <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg p-3 sm:p-4 border border-teal-100">
              <h4 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">
                {hasTrialEnded && user?.plan_name?.includes("Starter")
                  ? "Upgrade to Continue:"
                  : "What you'll unlock:"}
              </h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-600 flex-shrink-0" />
                  My Hub - Your personal AI assistant
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-600 flex-shrink-0" />
                  Advanced insights & analytics
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-600 flex-shrink-0" />
                  Priority support & more
                </li>
              </ul>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 flex-col sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setShowUpgradeModal(false)}
              className="w-full sm:w-auto"
            >
              Maybe Later
            </Button>
            <Button
              onClick={() => {
                setShowUpgradeModal(false);
                navigate("/settings?tab=subscription");
              }}
              className="bg-teal-600 hover:bg-teal-700 w-full sm:w-auto"
            >
              Upgrade Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coming Soon Modal */}
      <Dialog open={showComingSoonModal} onOpenChange={setShowComingSoonModal}>
        <DialogContent className="sm:max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Clock className="w-5 h-5 text-blue-500" /> Coming Soon!
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm sm:text-base">
              We're working hard to bring you seamless social media integration.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 sm:py-4">
            <div className="bg-gradient-to-r from-blue-50 to-blue-50 rounded-lg p-4 border border-blue-100">
              <div className="flex items-center justify-center mb-4">
                <Share2 className="w-16 h-16 text-blue-400" />
              </div>
              <h4 className="font-medium text-gray-900 mb-2 text-sm sm:text-base text-center">
                What's coming:
              </h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-500 flex-shrink-0" />
                  Direct social media posting
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-500 flex-shrink-0" />
                  Schedule posts in advance
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-500 flex-shrink-0" />
                  Multi-platform support
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-500 flex-shrink-0" />
                  AI-generated social content
                </li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowComingSoonModal(false)}
              className="w-full bg-blue-500 hover:bg-blue-600"
            >
              Got it, I'll wait!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logout Confirmation */}
      <AlertDialog
        open={showLogoutConfirm}
        onOpenChange={(open) => {
          if (open) {
            setShowLogoutConfirm(true);
            return;
          }
          closeLogoutConfirm();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to log out?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be logged out of your MyListIQ account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeLogoutConfirm}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                restoreBodyInteractivity();
                clearLogoutDialogTimer();
                localStorage.clear();
                sessionStorage.clear();
                navigate("/signup", { replace: true });
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Logout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
